import {
  ReactElement,
  ChangeEvent,
  useMemo,
  useCallback,
  useState,
} from 'react';
import {
  useTheme,
  Typography,
  Stack,
  Button,
  Collapse,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { LinkOff as UnLinkIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileArrowDown as ExportListIcon,
  faWarehouse as WarehouseIcon,
  faRoad as GenerateRouteIcon,
  faUserTie as DriverIcon,
  faVectorSquare as LogisticZoneIcon,
  faStoreAlt as CompanyIcon,
  faChartBar as TrackingIcon,
  faArrowsRotate as RefreshIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import {
  useAppSelector,
  useLazyRequest,
  useToggle,
  useValidatePartnerItems,
  useExportCollectedItems,
} from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isOnlyReadUser } from '~utils/user';
import { searchPartnerItemsToExportV2 } from '~services/partnerCompany';
import {
  ActionOrderMenu,
  DialogConfimation,
  DialogCancelPartnerItems,
  DialogGenerateRouteCollect,
} from '~components/index';
import { ActionOrderMenuOptionItem } from '~components/ActionOrderMenu/types';
import { Nullable } from '~globals/types/commons';
import {
  PlanFeaturesTypes,
  ReportCollectedItemsMode,
} from '~globals/types/enums';
import { formatterDate } from '~utils/formatter';
import { getLimitPremiunByVersion } from '~utils/feature';
import { downloadFileByRequest } from '~utils/download';

import { useDashboardVariantV2CarrierContext } from '../../../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../../../CollectFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentActionsContainer,
  DashboardVariantV2CarrierContentAccessFeatureButtonSwitch,
  DashboardVariantV2CarrierContentButtonSwitchInput,
  DashboardVariantV2CarrierContentFormControlWithBackground,
  DashboardVariantV2CarrierContentFormControlCheckBox,
  DashboardVariantV2CarrierContentActionsIconButton,
  DashboardVariantV2CarrierContentActionsIconButtonIcon,
} from '../../../../styles';

const DashboardVariantV2CarrierContentCollectTabActions = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const currentTheme = useTheme();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

  const {
    statistics: { reloadData: reloadStatistics },
  } = useDashboardVariantV2CarrierContext();

  const {
    filters: { handleClearFilters, handleClearFilterByValues },
    forceRequest: { toggleValue: toggleForceRequest },
  } = useDashboardVariantV2CarrierCollectFiltersContext();

  const {
    selectedPreset: { reset: handleResetSelectedPreset },
  } = useDashboardVariantV2CarrierPresetsContext();

  const {
    selection: { selectedIds, utils: selectedIdsUtils },
    parseFiltersRequest,
    table: {
      queryParams: { pagination },
    },
    actions: { reloadDataAndResetSelectedRows },
    showTracking: { value: showTracking, toggleValue: toggleShowTracking },
    isOnlyTodayDelivery: {
      value: isOnlyTodayDelivery,
      toggleValue: toggleIsOnlyTodayDelivery,
    },
  } = useDashboardVariantV2CarrierContentCollectTabContext();

  const isEmptySelectedIds = useMemo(
    () => selectedIdsUtils.isEmpty(),
    [selectedIdsUtils],
  );

  const handleChangeIsTodayDelivery = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target ?? event.currentTarget;
      toggleIsOnlyTodayDelivery(checked);

      let expirationDateTimeFrom: Nullable<Date> = null;
      let expirationDateTimeTo: Nullable<Date> = null;

      if (checked) {
        const currentDate = moment();
        expirationDateTimeFrom = currentDate.startOf('day').toDate();
        expirationDateTimeTo = currentDate.clone().endOf('day').toDate();
      }

      handleClearFilters(false);
      handleClearFilterByValues(
        { expirationDateTimeFrom, expirationDateTimeTo },
        () => toggleForceRequest(!showTracking),
      );
      handleResetSelectedPreset();
    },
    [
      handleClearFilterByValues,
      handleClearFilters,
      handleResetSelectedPreset,
      showTracking,
      toggleForceRequest,
      toggleIsOnlyTodayDelivery,
    ],
  );

  const [, loadingSearchToExport, , executeSearchToExport] = useLazyRequest({
    request: searchPartnerItemsToExportV2,
    withPostSuccess: (response) => {
      if (response.headers && response.data) {
        const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

        const defaultFileName = `colecta-${currentDate}.csv`;

        downloadFileByRequest(response.headers, response.data, defaultFileName);

        enqueueSnackbar(`Se ha exportado los pedidos correctamente`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      }
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleExportItems = useCallback(async () => {
    await executeSearchToExport({
      filters: parseFiltersRequest(),
      pagination,
    });
  }, [executeSearchToExport, pagination, parseFiltersRequest]);

  const [openConfirmValidateOrders, toggleOpenConfirmValidateOrders] =
    useToggle(false);

  const [openDialogCancelOrders, toggleOpenDialogCancelOrders] =
    useToggle(false);

  const actionsWitSelectionMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
    () => [
      {
        label: 'Marcar como recepcionados',
        Icon: (
          <FontAwesomeIcon
            icon={WarehouseIcon}
            color={currentTheme.palette.success.main}
          />
        ),
        enabled: !isUserOnlyReader,
        onClick: () => toggleOpenConfirmValidateOrders(true),
      },
      {
        label: 'Desasignar colecta de pedidos',
        Icon: <UnLinkIcon color="error" />,
        enabled: !isUserOnlyReader,
        onClick: () => toggleOpenDialogCancelOrders(true),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isUserOnlyReader,
      loadingSearchToExport,
      handleExportItems,
      toggleOpenConfirmValidateOrders,
      toggleOpenDialogCancelOrders,
    ],
  );

  const actionsGeneralMenuOptionsNormal = useMemo<ActionOrderMenuOptionItem[]>(
    () => [
      {
        label: 'Exportar listado',
        Icon: <FontAwesomeIcon icon={ExportListIcon} />,
        loading: loadingSearchToExport,
        enabled: true,
        disabled: false,
        onClick: handleExportItems,
      },
    ],
    [loadingSearchToExport, handleExportItems],
  );

  const { loading: loadingExportCollectedItems, onExportCollectedItems } =
    useExportCollectedItems();
  const [currentModeExportCollectedItems, setCurrentModeExportCollectedItems] =
    useState<Nullable<ReportCollectedItemsMode>>(null);

  const getIsLoadingExportCollectedItemsByMode = useCallback(
    (mode: ReportCollectedItemsMode) =>
      loadingExportCollectedItems && currentModeExportCollectedItems === mode,
    [currentModeExportCollectedItems, loadingExportCollectedItems],
  );

  const handleExportCollectedItems = useCallback(
    async (mode: ReportCollectedItemsMode) => {
      setCurrentModeExportCollectedItems(mode);

      await onExportCollectedItems({
        reportCollectedItemsMode: mode,
        onlyTodayDelivery: isOnlyTodayDelivery,
      });

      setCurrentModeExportCollectedItems(null);
    },
    [onExportCollectedItems, isOnlyTodayDelivery],
  );

  const actionsGeneralMenuOptionsTracking = useMemo<
    ActionOrderMenuOptionItem[]
  >(
    () => [
      {
        label: 'Descargar seguimiento por choferes',
        Icon: <FontAwesomeIcon icon={DriverIcon} />,
        loading: getIsLoadingExportCollectedItemsByMode(
          ReportCollectedItemsMode.Drivers,
        ),
        enabled: true,
        disabled: false,
        onClick: () =>
          handleExportCollectedItems(ReportCollectedItemsMode.Drivers),
      },
      {
        label: 'Descargar seguimiento por zonas',
        Icon: <FontAwesomeIcon icon={LogisticZoneIcon} />,
        loading: getIsLoadingExportCollectedItemsByMode(
          ReportCollectedItemsMode.Zones,
        ),
        enabled: true,
        disabled: false,
        onClick: () =>
          handleExportCollectedItems(ReportCollectedItemsMode.Zones),
      },
      {
        label: 'Descargar seguimiento por empresas',
        Icon: <FontAwesomeIcon icon={CompanyIcon} />,
        loading: getIsLoadingExportCollectedItemsByMode(
          ReportCollectedItemsMode.Companies,
        ),
        enabled: true,
        disabled: false,
        onClick: () =>
          handleExportCollectedItems(ReportCollectedItemsMode.Companies),
      },
    ],
    [getIsLoadingExportCollectedItemsByMode, handleExportCollectedItems],
  );

  const actionsGeneralMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
    () =>
      showTracking
        ? actionsGeneralMenuOptionsTracking
        : actionsGeneralMenuOptionsNormal,
    [
      actionsGeneralMenuOptionsNormal,
      actionsGeneralMenuOptionsTracking,
      showTracking,
    ],
  );

  const handleSuccessValidateOrders = useCallback(() => {
    toggleOpenConfirmValidateOrders(false);

    reloadDataAndResetSelectedRows();
  }, [toggleOpenConfirmValidateOrders, reloadDataAndResetSelectedRows]);

  const {
    loading: loadingValidateOrders,
    handleValidatePartnerItems: executeValidateOrders,
  } = useValidatePartnerItems(handleSuccessValidateOrders);

  const handleValidateOrders = useCallback(async () => {
    await executeValidateOrders(selectedIds);
  }, [executeValidateOrders, selectedIds]);

  const [openDialogGenerateRouteCollect, toggleOpenDialogGenerateRouteCollect] =
    useToggle(false);

  const handleRefreshData = useCallback(() => {
    reloadDataAndResetSelectedRows();
    reloadStatistics(false);
  }, [reloadStatistics, reloadDataAndResetSelectedRows]);

  return (
    <DashboardVariantV2CarrierContentActionsContainer justifyContent="space-between">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography component="h6" variant="h6" fontWeight="bold">
          PEDIDOS EN COLECTA
        </Typography>

        {!showTracking && (
          <Tooltip title="Refrescar listado" arrow>
            <DashboardVariantV2CarrierContentActionsIconButton
              color="secondary"
              variant="outlined"
              onClick={handleRefreshData}
            >
              <DashboardVariantV2CarrierContentActionsIconButtonIcon
                icon={RefreshIcon}
              />
            </DashboardVariantV2CarrierContentActionsIconButton>
          </Tooltip>
        )}

        {!isUserOnlyReader && (
          <Collapse
            in={!isEmptySelectedIds && !showTracking}
            timeout="auto"
            unmountOnExit
            orientation="horizontal"
          >
            <ActionOrderMenu
              options={actionsWitSelectionMenuOptions}
              buttonProps={{ label: 'Selección', sx: { minHeight: 40 } }}
            />
          </Collapse>
        )}
      </Stack>

      <Stack direction="row" spacing={2}>
        {!isUserOnlyReader && !showTracking && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<FontAwesomeIcon icon={GenerateRouteIcon} />}
            onClick={() => toggleOpenDialogGenerateRouteCollect(true)}
            disabled={isEmptySelectedIds}
            sx={{ display: 'none' }}
          >
            Generar plan de colecta
          </Button>
        )}

        <DashboardVariantV2CarrierContentFormControlWithBackground
          control={
            <DashboardVariantV2CarrierContentFormControlCheckBox
              checked={isOnlyTodayDelivery}
              onChange={handleChangeIsTodayDelivery}
            />
          }
          label="Solo envíos que vencen hoy"
        />

        {!isUserOnlyReader && (
          <>
            <ActionOrderMenu options={actionsGeneralMenuOptions} />

            <DashboardVariantV2CarrierContentAccessFeatureButtonSwitch
              validFeature={PlanFeaturesTypes.LimitPremium}
              availableToDate={getLimitPremiunByVersion('1.24.0')}
              activeForVersion="1.24.0"
              variant="outlined"
              color="primary"
              startIcon={<FontAwesomeIcon icon={TrackingIcon} />}
              onClick={() => toggleShowTracking()}
              disableRipple
              disableFocusRipple
              disableTouchRipple
              sx={{ borderColor: 'transparent' }}
            >
              <FormControlLabel
                control={
                  <DashboardVariantV2CarrierContentButtonSwitchInput
                    checked={showTracking}
                  />
                }
                label="Ver Seguimiento"
                labelPlacement="start"
                componentsProps={{
                  typography: {
                    variant: 'inherit',
                  },
                }}
                sx={{ marginLeft: 0 }}
              />
            </DashboardVariantV2CarrierContentAccessFeatureButtonSwitch>
          </>
        )}
      </Stack>

      <DialogConfimation
        open={openConfirmValidateOrders}
        description="Los pedidos seleccionados serán marcado como recepcionados"
        onConfirm={handleValidateOrders}
        onCancel={() => toggleOpenConfirmValidateOrders(false)}
        loadingConfirm={loadingValidateOrders}
      />

      {openDialogCancelOrders && (
        <DialogCancelPartnerItems
          open={openDialogCancelOrders}
          onClose={() => toggleOpenDialogCancelOrders(false)}
          partnerCompaniesIds={selectedIds}
          onCancelSuccess={reloadDataAndResetSelectedRows}
        />
      )}

      {openDialogGenerateRouteCollect && (
        <DialogGenerateRouteCollect
          open={openDialogGenerateRouteCollect}
          onClose={() => toggleOpenDialogGenerateRouteCollect(false)}
          orderIds={selectedIds}
          onSuccessComplete={() => reloadDataAndResetSelectedRows()}
        />
      )}
    </DashboardVariantV2CarrierContentActionsContainer>
  );
};

export default DashboardVariantV2CarrierContentCollectTabActions;
